import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, FileText, User, Phone, Mail, Calendar as CalendarIcon, ArrowRight, ClipboardList, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const isAdminEmail = (email) => {
    if (!email) return false;
    const e = String(email).toLowerCase().trim();
    return e === 'luxymove@gmail.com' || e === 'admin@luxymove.com';
};

function formatDate(iso) {
    if (!iso) return '-';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return '-'; }
}

function daysSince(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const now = new Date();
        const startOfDay = (x) => { const y = new Date(x); y.setHours(0,0,0,0); return y; };
        const diff = Math.floor((startOfDay(now) - startOfDay(d)) / (1000 * 60 * 60 * 24));
        return diff;
    } catch { return null; }
}

export default function DevisNonEnvoyes() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = !!user && (user.role === 'admin' || isAdminEmail(user.email));
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const handleDelete = async (it, e) => {
        e.stopPropagation();
        if (!isAdmin) return;
        if (!window.confirm(`Retirer "${it.nom}" de la liste des devis non envoyés ? Le prospect et ses autres données (agenda, devis…) ne sont pas affectés.`)) return;
        try {
            // Scope = only the inventaire card driving this list.
            // Calling /api/prospects/{id} previously was both too destructive
            // (wiped the prospect everywhere) and brittle (returned 404 on
            // orphan rows, blocking cleanup). Targeting the inventaire card
            // directly fixes both issues at once.
            await axios.delete(`${API_URL}/api/inventaire-prospects/${it.id}`, { withCredentials: true });
            toast.success('Fiche retirée');
            setItems(prev => prev.filter(x => x.id !== it.id));
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API_URL}/api/dashboard/devis-non-envoyes`, { withCredentials: true });
                setItems(res.data || []);
            } catch {
                toast.error('Erreur de chargement');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <Layout>
            <div className="space-y-6" data-testid="devis-non-envoyes-page">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/')} className="flex items-center gap-1 text-gray-500 hover:text-[#1B365D] transition-colors text-sm">
                        <ArrowLeft className="w-4 h-4" /> Retour
                    </button>
                    <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Devis non envoyé
                    </h1>
                    <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 ml-1">{items.length}</Badge>
                </div>

                <p className="text-sm text-gray-500">
                    Inventaires dont le devis n'a pas encore été envoyé au client. Ils apparaissent ici dès la création de l'inventaire et disparaissent automatiquement une fois le devis envoyé.
                </p>

                <Card className="shadow-sm">
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="flex items-center justify-center py-16">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1B365D]" />
                            </div>
                        ) : items.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <div className="h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                                    <ClipboardList className="h-8 w-8 text-emerald-500" />
                                </div>
                                <p className="text-gray-500 font-medium">Tous les devis sont à jour</p>
                                <p className="text-gray-400 text-sm mt-1">Aucun inventaire en attente d'envoi</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {items.map((it) => {
                                    const days = daysSince(it.created_at);
                                    return (
                                        <div
                                            key={it.id}
                                            className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                                            onClick={() => navigate(`/prospects/${it.prospect_id}?context=extended&from=inventaire`)}
                                            data-testid={`row-inv-${it.id}`}
                                        >
                                            <div className="h-10 w-10 rounded-full bg-[#1B365D]/10 flex items-center justify-center shrink-0">
                                                <User className="h-5 w-5 text-[#1B365D]" />
                                            </div>
                                            <div className="flex-1 min-w-0 space-y-0.5">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="font-semibold text-[#1B365D] truncate">{it.nom || 'Sans nom'}</p>
                                                    {days !== null && days >= 1 && (
                                                        <Badge className={`text-[10px] border ${days >= 3 ? 'bg-red-100 text-red-700 border-red-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>
                                                            {days === 1 ? '1 jour de retard' : `${days} jours de retard`}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                                                    {it.telephone && (
                                                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {it.telephone}</span>
                                                    )}
                                                    {it.email && (
                                                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {it.email}</span>
                                                    )}
                                                    <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> Créé le {formatDate(it.created_at)}</span>
                                                    {it.commercial_name && (
                                                        <span className="inline-flex items-center gap-1">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                                            {it.commercial_name}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white gap-1 shrink-0"
                                                onClick={(e) => { e.stopPropagation(); navigate(`/prospects/${it.prospect_id}?context=extended&from=inventaire`); }}
                                                data-testid={`view-inv-${it.id}`}
                                            >
                                                <FileText className="w-3.5 h-3.5" />
                                                Faire le devis
                                                <ArrowRight className="w-3.5 h-3.5" />
                                            </Button>
                                            {isAdmin && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                                                    onClick={(e) => handleDelete(it, e)}
                                                    title="Supprimer (admin uniquement)"
                                                    data-testid={`delete-inv-${it.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
